/**
 * FeeManagement Component
 * 협력사 → 본사 수수료 관리
 * 본사가 협력사에게 설정하는 수수료 (일정산/주정산)
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ToastType} from "@/shared/components/feedback/Toast/Toast.types";
import {useToast} from "@/shared/components/feedback/Toast/ToastContext";
import {usePagination} from '@/shared/hooks/usePagination';
import {loadFilters, saveFilters} from '@/shared/utils/filterStorage';
import Loading from '@/shared/components/feedback/Loading';
import {useUserInfo} from '@/shared/hooks/useCrudOperations';
import {supplierCommissionApi, SaveSupplierCommissionResponse} from '../api/supplierCommissionApi';
import {supplierApi} from "@/features/partner/supplierStatus/api/SupplierApi";
import {commonCodeApi} from "@/shared/api/commonCodeApi";
import FilterSection from './FilterSection';
import CommissionTable from './CommissionTable';
import HistoryModal from './HistoryModal';
import FeeApplyDateModal from '@/features/partner/supplierFee/riderToPartner/components/FeeApplyDateModal';
import {
    applyDefaultValuesToAll,
    FILTER_STORAGE_KEY,
    filterCommissionData,
    getUniqueSupplierNames,
    ITEMS_PER_PAGE,
    sortByStatus,
    transformCommissionData
} from '../lib/feeManagement.helpers';
import {CommissionHistoryItem, PeriodType, SupplierCommissionRow} from '../types/feeManagement.types';
import '@/shared/styles/common-table.css';

// 필터 기본값
const DEFAULT_FILTER = {
    ALL: '전체',
    ALL_SUPPLIER_RIDERS: '전협력사 라이더'
} as const;

const FeeManagement: React.FC = () => {
    // ======== Hooks ========
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();
    const hasAppliedInitialFilter = useRef(false);

    // ======== State ========
    const [loading, setLoading] = useState<boolean>(true);
    const [commissionData, setCommissionData] = useState<SupplierCommissionRow[]>([]);
    const [allCommissionData, setAllCommissionData] = useState<SupplierCommissionRow[]>([]);

    // 기본 수수료 금액 (vr_comm_code에서 조회)
    const [defaultDailyAmount, setDefaultDailyAmount] = useState<string>('0');
    const [defaultWeeklyAmount, setDefaultWeeklyAmount] = useState<string>('0');

    // 모달 상태
    const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
    const [selectedSupplier, setSelectedSupplier] = useState<string>('');
    const [historyData, setHistoryData] = useState<CommissionHistoryItem[]>([]);

    // 수수료 적용 예정일 모달 상태
    const [showFeeApplyModal, setShowFeeApplyModal] = useState<boolean>(false);
    const [feeApplyDateInfo, setFeeApplyDateInfo] = useState<{
        applyStartDate: string;
        isImmediateApply: boolean;
    } | null>(null);

    // 필터 상태 (저장된 값 복원)
    const savedFilters = loadFilters<{
        partnerId: string;
        statusFilter: string;
    }>(FILTER_STORAGE_KEY);

    const [partnerId, setPartnerId] = useState<string>(savedFilters?.partnerId || DEFAULT_FILTER.ALL);
    const [statusFilter, setStatusFilter] = useState<string>(savedFilters?.statusFilter || '');

    // 페이지네이션
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: commissionData, itemsPerPage: ITEMS_PER_PAGE });

    // ======== Derived Values ========
    const supplierNames = getUniqueSupplierNames(allCommissionData);

    // ======== Effects ========
    /** 필터 변경 시 저장 */
    useEffect(() => {
        saveFilters(FILTER_STORAGE_KEY, {
            partnerId,
            statusFilter
        });
    }, [partnerId, statusFilter]);

    /** 초기 데이터 로드 */
    useEffect(() => {
        fetchDefaultFeeAmounts();
        fetchSuppliers();
    }, []);

    /** 저장된 필터로 초기 검색 */
    useEffect(() => {
        if (hasAppliedInitialFilter.current) return;
        if (!loading && allCommissionData.length > 0) {
            const saved = loadFilters<typeof savedFilters>(FILTER_STORAGE_KEY);
            if (saved) {
                const hasFilter = saved.partnerId && saved.partnerId !== DEFAULT_FILTER.ALL;
                if (hasFilter) {
                    hasAppliedInitialFilter.current = true;
                    handleSearch();
                }
            }
        }
    }, [loading, allCommissionData.length]);

    // ======== API Calls ========
    /** vr_comm_code에서 기본 수수료 금액 조회 */
    const fetchDefaultFeeAmounts = async () => {
        try {
            const response = await commonCodeApi.getCommonCodeList('FEE_NAME');

            // HQPRE: 본사 일정산 수수료
            const hqPreCode = response.codes.find(code => code.comCode === 'HQPRE');
            if (hqPreCode && hqPreCode.val3) {
                setDefaultDailyAmount(hqPreCode.val3);
            }

            // HQPOST: 본사 주정산 수수료
            const hqPostCode = response.codes.find(code => code.comCode === 'HQPOST');
            if (hqPostCode && hqPostCode.val3) {
                setDefaultWeeklyAmount(hqPostCode.val3);
            }
        } catch {
            // 기본값 유지 (0원)
        }
    };

    /** 협력사 수수료 목록 조회 */
    const fetchSuppliers = async () => {
        try {
            setLoading(true);
            // 수수료 정보와 협력사 상세 정보 병렬 조회
            const [commissions, supplierDetails] = await Promise.all([
                supplierCommissionApi.getAllSupplierCommissions(),
                supplierApi.getSupplierDetailList()
            ]);

            // 협력사 상태 정보를 Map으로 변환
            const statusMap = new Map(
                supplierDetails.suppliers.map(s => [s.suppId, s.statusCode])
            );

            // 수수료 데이터에 상태 정보 추가
            const commissionsWithStatus = commissions.map(comm => ({
                ...comm,
                status: statusMap.get(comm.supplierId) || 'OPERATING'
            }));

            const initialData = transformCommissionData(commissionsWithStatus);
            // 상태별로 정렬: 운영중 -> 계약종료 접수 -> 계약종료
            const sortedData = sortByStatus(initialData);

            setAllCommissionData(sortedData);
            setCommissionData(sortedData);
        } catch {
            showToast('본사 수수료를 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    };

    /** 수수료 저장 */
    const saveCommission = async (row: SupplierCommissionRow): Promise<SaveSupplierCommissionResponse | null> => {
        const userInfo = getUserInfo();
        if (!userInfo) {
            showToast('사용자 정보를 가져올 수 없습니다.', { type: ToastType.ERROR });
            return null;
        }

        try {
            const response = await supplierCommissionApi.saveSupplierCommission({
                suppId: row.supplierId,
                dailyCommissionType: row.dailyCommissionType || null,
                dailyAmount: row.dailyAmount ? parseInt(row.dailyAmount.replace(/,/g, '')) : null,
                weeklyCommissionType: row.weeklyCommissionType || null,
                weeklyAmount: row.weeklyAmount ? parseInt(row.weeklyAmount.replace(/,/g, '')) : null,
                weeklyPaybackUseYn: row.weeklyPaybackUseYn || 'N',
                weeklyPaybackAmount: row.weeklyPaybackAmount ? parseInt(row.weeklyPaybackAmount.replace(/,/g, '')) : null,
                startDate: row.startDate,
                endDate: row.endDate || null,
                userId: userInfo.userId
            });
            return response;
        } catch {
            showToast('수수료 저장에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        }
    };

    /** 히스토리 조회 */
    const fetchHistory = async (supplierId: string) => {
        try {
            const history = await supplierCommissionApi.getCommissionHistory(supplierId);
            return history;
        } catch {
            showToast('히스토리를 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            return [];
        }
    };

    // ======== Event Handlers ========
    /** 검색 실행 */
    const handleSearch = useCallback(() => {
        const filtered = filterCommissionData(allCommissionData, partnerId, statusFilter);

        setCommissionData(filtered);
        resetPagination();

        if (filtered.length === 0) {
            showToast('조회된 데이터가 없습니다.', { type: ToastType.INFO });
        } else {
            showToast(`${filtered.length}개의 협력사가 조회되었습니다.`, { type: ToastType.SUCCESS });
        }
    }, [allCommissionData, partnerId, statusFilter, resetPagination, showToast]);

    /** 빠른 필터 (상태 버튼 클릭 시 즉시 필터링) */
    const handleQuickFilter = useCallback((status: string) => {
        setStatusFilter(status);
        const filtered = filterCommissionData(allCommissionData, partnerId, status);
        setCommissionData(filtered);
        resetPagination();
    }, [allCommissionData, partnerId, resetPagination]);

    /** 모든 협력사에 초기값 일괄 설정 */
    const handleSetDefaultValues = useCallback(() => {
        setCommissionData(prev => applyDefaultValuesToAll(prev, defaultDailyAmount, defaultWeeklyAmount));
        showToast('모든 협력사에 초기값이 설정되었습니다.', { type: ToastType.SUCCESS });
    }, [defaultDailyAmount, defaultWeeklyAmount, showToast]);

    /** 수수료변경 토글 (새 수수료 입력 행 표시/숨김) */
    const handleToggleFeeChange = useCallback((supplierId: string) => {
        setCommissionData(prev => prev.map(row => {
            if (row.supplierId !== supplierId) return row;

            // 최소 시 (isAddingNew가 true -> false): 입력값 초기화
            if (row.isAddingNew) {
                return {
                    ...row,
                    isAddingNew: false,
                    newDailyCommissionType: undefined,
                    newDailyAmount: undefined,
                    newWeeklyCommissionType: undefined,
                    newWeeklyAmount: undefined,
                    newWeeklyPaybackUseYn: undefined,
                    newWeeklyPaybackAmount: undefined,
                    newStartDate: undefined,
                    isEdited: false
                };
            }

            // 변경 시 (isAddingNew가 false -> true): 기존 값 복사
            return {
                ...row,
                isAddingNew: true,
                newDailyCommissionType: row.dailyCommissionType || '',
                newDailyAmount: row.dailyAmount || '',
                newWeeklyCommissionType: row.weeklyCommissionType || '',
                newWeeklyAmount: row.weeklyAmount || '',
                newWeeklyPaybackUseYn: row.weeklyPaybackUseYn || 'N',
                newWeeklyPaybackAmount: row.weeklyPaybackAmount || '',
                newStartDate: row.startDate || '',
                isEdited: true
            };
        }));
    }, []);

    /** 모든 협력사에 페이백 초기값 일괄 설정 (30원, 사용) */
    const handleSetDefaultPayback = useCallback(() => {
        const DEFAULT_PAYBACK_AMOUNT = '30';
        setCommissionData(prev => prev.map(row => {
            // 운영중(OPERATING)이 아닌 상태는 초기값 설정 제외
            if (row.status !== 'OPERATING') {
                return row;
            }
            return {
                ...row,
                // 기존 수수료 정보 복사
                newDailyCommissionType: row.dailyCommissionType || '',
                newDailyAmount: row.dailyAmount || '',
                newWeeklyCommissionType: row.weeklyCommissionType || '',
                newWeeklyAmount: row.weeklyAmount || '',
                newStartDate: row.startDate || '',
                // 페이백 초기값 설정
                newWeeklyPaybackUseYn: 'Y',
                newWeeklyPaybackAmount: DEFAULT_PAYBACK_AMOUNT,
                isAddingNew: true,
                isEdited: true
            };
        }));
        showToast('모든 협력사에 페이백 초기값(30원, 사용)이 설정되었습니다.', { type: ToastType.SUCCESS });
    }, [showToast]);

    /** 새 수수료 구분 변경 */
    const handleNewCommissionTypeChange = useCallback((
        supplierId: string,
        period: PeriodType,
        value: string
    ) => {
        setCommissionData(prev => prev.map(row => {
            if (row.supplierId !== supplierId) return row;
            if (period === 'daily') {
                return { ...row, newDailyCommissionType: value, newDailyAmount: '', isEdited: true };
            }
            return { ...row, newWeeklyCommissionType: value, newWeeklyAmount: '', isEdited: true };
        }));
    }, []);

    /** 새 금액 변경 */
    const handleNewAmountChange = useCallback((
        supplierId: string,
        period: PeriodType,
        value: string
    ) => {
        setCommissionData(prev => prev.map(row => {
            if (row.supplierId !== supplierId) return row;
            return {
                ...row,
                [period === 'daily' ? 'newDailyAmount' : 'newWeeklyAmount']: value,
                isEdited: true
            };
        }));
    }, []);

    /** 새 시작일자 변경 */
    const handleNewStartDateChange = useCallback((supplierId: string, value: string) => {
        setCommissionData(prev => prev.map(row =>
            row.supplierId === supplierId ? { ...row, newStartDate: value, isEdited: true } : row
        ));
    }, []);

    /** 페이백 사용여부 변경 */
    const handleNewPaybackUseYnChange = useCallback((supplierId: string, value: string) => {
        setCommissionData(prev => prev.map(row => {
            if (row.supplierId !== supplierId) return row;
            return {
                ...row,
                newWeeklyPaybackUseYn: value,
                newWeeklyPaybackAmount: value === 'N' ? '' : row.newWeeklyPaybackAmount,
                isEdited: true
            };
        }));
    }, []);

    /** 페이백 금액 변경 */
    const handleNewPaybackAmountChange = useCallback((supplierId: string, value: string) => {
        setCommissionData(prev => prev.map(row =>
            row.supplierId === supplierId ? { ...row, newWeeklyPaybackAmount: value, isEdited: true } : row
        ));
    }, []);

    /** 저장 버튼 클릭 (새 수수료 저장) */
    const handleSave = useCallback(async (supplierId: string) => {
        const row = commissionData.find(r => r.supplierId === supplierId);
        if (!row || !row.isAddingNew) return;

        // 유효성 검사: 새 시작일자 필수
        if (!row.newStartDate) {
            showToast('적용일을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        const hasNewDailyCommission = row.newDailyCommissionType && row.newDailyAmount;
        const hasNewWeeklyCommission = row.newWeeklyCommissionType && row.newWeeklyAmount;

        if (!hasNewDailyCommission && !hasNewWeeklyCommission) {
            showToast('일정산 또는 주정산 수수료를 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        // 새 수수료로 저장
        const saveData = {
            ...row,
            dailyCommissionType: row.newDailyCommissionType || row.dailyCommissionType,
            dailyAmount: row.newDailyAmount || row.dailyAmount,
            weeklyCommissionType: row.newWeeklyCommissionType || row.weeklyCommissionType,
            weeklyAmount: row.newWeeklyAmount || row.weeklyAmount,
            weeklyPaybackUseYn: row.newWeeklyPaybackUseYn ?? row.weeklyPaybackUseYn ?? 'N',
            weeklyPaybackAmount: row.newWeeklyPaybackAmount || row.weeklyPaybackAmount || '',
            startDate: row.newStartDate
        };

        const response = await saveCommission(saveData);
        if (response) {
            showToast('수수료가 저장되었습니다.', { type: ToastType.SUCCESS });
            // 저장 후 데이터 새로고침
            await fetchSuppliers();

            // 적용 예정일 모달 표시
            setFeeApplyDateInfo({
                applyStartDate: response.applyStartDate,
                isImmediateApply: response.isImmediateApply
            });
            setShowFeeApplyModal(true);
        }
    }, [commissionData, showToast]);

    /** 일괄 저장 (변경된 모든 협력사 수수료 저장) */
    const handleSaveAll = useCallback(async () => {
        // 변경된 행만 필터링 (isAddingNew && isEdited)
        const editedRows = commissionData.filter(row => row.isAddingNew && row.isEdited);

        if (editedRows.length === 0) {
            showToast('저장할 변경사항이 없습니다.', { type: ToastType.INFO });
            return;
        }

        // 유효성 검사
        const invalidRows = editedRows.filter(row => {
            const hasNewDailyCommission = row.newDailyCommissionType && row.newDailyAmount;
            const hasNewWeeklyCommission = row.newWeeklyCommissionType && row.newWeeklyAmount;
            return !row.newStartDate || (!hasNewDailyCommission && !hasNewWeeklyCommission);
        });

        if (invalidRows.length > 0) {
            showToast(`${invalidRows.length}개 협력사의 적용일 또는 수수료 정보가 누락되었습니다.`, { type: ToastType.WARNING });
            return;
        }

        // 모든 행 저장
        let successCount = 0;
        let failCount = 0;
        let lastResponse: SaveSupplierCommissionResponse | null = null;

        for (const row of editedRows) {
            const saveData = {
                ...row,
                dailyCommissionType: row.newDailyCommissionType || row.dailyCommissionType,
                dailyAmount: row.newDailyAmount || row.dailyAmount,
                weeklyCommissionType: row.newWeeklyCommissionType || row.weeklyCommissionType,
                weeklyAmount: row.newWeeklyAmount || row.weeklyAmount,
                weeklyPaybackUseYn: row.newWeeklyPaybackUseYn ?? row.weeklyPaybackUseYn ?? 'N',
                weeklyPaybackAmount: row.newWeeklyPaybackAmount || row.weeklyPaybackAmount || '',
                startDate: row.newStartDate || ''
            };

            const response = await saveCommission(saveData);
            if (response) {
                successCount++;
                lastResponse = response;
            } else {
                failCount++;
            }
        }

        if (failCount === 0) {
            showToast(`${successCount}개 협력사 수수료가 저장되었습니다.`, { type: ToastType.SUCCESS });
        } else {
            showToast(`${successCount}개 성공, ${failCount}개 실패`, { type: ToastType.WARNING });
        }

        // 저장 후 데이터 새로고침
        await fetchSuppliers();

        // 적용 예정일 모달 표시 (마지막 성공 응답 기준)
        if (lastResponse) {
            setFeeApplyDateInfo({
                applyStartDate: lastResponse.applyStartDate,
                isImmediateApply: lastResponse.isImmediateApply
            });
            setShowFeeApplyModal(true);
        }
    }, [commissionData, showToast]);

    /** 히스토리 조회 */
    const handleShowHistory = useCallback(async (supplierId: string, supplierName: string) => {
        const history = await fetchHistory(supplierId);
        setHistoryData(history);
        setSelectedSupplier(supplierName);
        setShowHistoryModal(true);
    }, []);

    // ======== Render ========
    if (loading) {
        return <Loading message="수수료 데이터를 불러오는 중..." />;
    }

    return (
        <>
            {/* 필터 섹션 */}
            <FilterSection
                partnerId={partnerId}
                supplierNames={supplierNames}
                statusFilter={statusFilter}
                onPartnerChange={setPartnerId}
                onStatusChange={setStatusFilter}
                onSearch={handleSearch}
                onQuickFilter={handleQuickFilter}
            />

            {/* 협력사 수 */}
            <div className="result-count">
                <i className="fas fa-list"></i>
                총 <strong>{commissionData.length.toLocaleString()}개</strong> 협력사
            </div>

            {/* 수수료 테이블 */}
            <CommissionTable
                currentItems={currentItems}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                onSetDefaultValues={handleSetDefaultValues}
                onSetDefaultPayback={handleSetDefaultPayback}
                onNewCommissionTypeChange={handleNewCommissionTypeChange}
                onNewAmountChange={handleNewAmountChange}
                onNewPaybackUseYnChange={handleNewPaybackUseYnChange}
                onNewPaybackAmountChange={handleNewPaybackAmountChange}
                onNewStartDateChange={handleNewStartDateChange}
                onShowHistory={handleShowHistory}
                onSave={handleSave}
                onSaveAll={handleSaveAll}
                onToggleFeeChange={handleToggleFeeChange}
            />

            {/* 히스토리 모달 */}
            <HistoryModal
                isOpen={showHistoryModal}
                supplierName={selectedSupplier}
                historyData={historyData}
                onClose={() => setShowHistoryModal(false)}
            />

            {/* 수수료 적용 예정일 모달 */}
            {feeApplyDateInfo && (
                <FeeApplyDateModal
                    isOpen={showFeeApplyModal}
                    applyStartDate={feeApplyDateInfo.applyStartDate}
                    isImmediateApply={feeApplyDateInfo.isImmediateApply}
                    onClose={() => {
                        setShowFeeApplyModal(false);
                        setFeeApplyDateInfo(null);
                    }}
                />
            )}
        </>
    );
};

export default FeeManagement;
